import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

// Official Google Meet icon (simplified)
const GoogleMeetIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 87.5 72" xmlns="http://www.w3.org/2000/svg">
    <path fill="#00832d" d="M49.5 36l8.53 9.75 11.47 7.33 2-17.02-2-16.64-11.69 6.44z"/>
    <path fill="#0066da" d="M0 51.5V66c0 3.315 2.685 6 6 6h14.5l3-10.96-3-9.54-9.95-3z"/>
    <path fill="#e94235" d="M20.5 0L0 20.5l10.55 3 9.95-3 2.95-9.41z"/>
    <path fill="#2684fc" d="M20.5 20.5H0v31h20.5z"/>
    <path fill="#00ac47" d="M82.6 8.68L69.5 19.42v33.66l13.16 10.79c1.97 1.54 4.85.135 4.85-2.37V11c0-2.535-2.945-3.925-4.91-2.32zM49.5 36v15.5h-29V72h43c3.315 0 6-2.685 6-6V53.08z"/>
    <path fill="#ffba00" d="M63.5 0h-43v20.5h29V36l20-16.57V6c0-3.315-2.685-6-6-6z"/>
  </svg>
);

export const CallBand = ({ onBookCall }) => {
  return (
    <div className="relative bg-[#050510] border-b border-white/5 max-h-[160px] md:max-h-none overflow-hidden">
      {/* Glowing gradient border effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-[#7c3aed] via-[#06b6d4] to-[#7c3aed] opacity-50" />
        <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-[#7c3aed]/5 to-transparent" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 py-3 md:py-5">
        {/* Desktop layout */}
        <div className="hidden md:flex items-center gap-6">
          {/* Photo with pulsing ring and Meet badge */}
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 rounded-full animate-pulse-ring" />
            <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-[#7c3aed]/50">
              <img 
                src="/founder.jpg" 
                alt="Sahil Aswani" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#0a0a12] border border-white/20 flex items-center justify-center">
              <GoogleMeetIcon className="w-4 h-4" />
            </div>
          </div>

          {/* Text content */}
          <div className="flex-1">
            <h3 className="font-heading font-bold text-white text-lg mb-1">
              Talk to the founder, live on Google Meet
            </h3>
            <p className="text-white/50 text-sm">
              Free 20-minute 1-on-1 with Sahil. Understand, verify, plan, and clear every question.
            </p>
          </div>

          {/* CTA Button */}
          <motion.button
            onClick={onBookCall}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="group flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] text-white font-heading font-semibold text-sm shadow-lg shadow-[#7c3aed]/20 hover:shadow-[#7c3aed]/40 transition-all"
            data-testid="call-band-cta"
          >
            See if you qualify
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </div>

        {/* Mobile layout - two rows */}
        <div className="flex md:hidden flex-col gap-2">
          {/* Row 1: Photo + Headline */}
          <div className="flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 rounded-full animate-pulse-ring" />
              <div className="relative w-11 h-11 rounded-full overflow-hidden border-2 border-[#7c3aed]/50">
                <img 
                  src="/founder.jpg" 
                  alt="Sahil Aswani" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#0a0a12] border border-white/20 flex items-center justify-center">
                <GoogleMeetIcon className="w-3 h-3" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-bold text-white text-sm leading-tight">
                Talk to the founder, live on Google Meet
              </h3>
              <p className="text-white/50 text-xs mt-0.5">
                Free · 20 minutes · 1-on-1 with Sahil
              </p>
            </div>
          </div>
          
          {/* Row 2: CTA Button */}
          <motion.button
            onClick={onBookCall}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="group flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] text-white font-heading font-semibold text-sm shadow-lg shadow-[#7c3aed]/20"
            data-testid="call-band-cta"
          >
            See if you qualify
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </div>
      </div>

      {/* CSS for pulsing animation */}
      <style>{`
        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 0.5;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.2;
          }
          100% {
            transform: scale(1);
            opacity: 0.5;
          }
        }
        .animate-pulse-ring {
          animation: pulse-ring 2s ease-in-out infinite;
          background: radial-gradient(circle, rgba(124, 58, 237, 0.3) 0%, transparent 70%);
        }
      `}</style>
    </div>
  );
};

export default CallBand;
